import config from "../../config";
import { seoContent } from "../../constants/SEOConstant";
import { GuidesPage } from "./GuidesPage";
import {
    LinksFromStreamtoStreamForSync,
    LinksFromStreamtoStreamForTransfer,
    TransferLinksDataFromStreamtoStream,
} from "./LinksFromStreamtoStream";

export default function TidalGuide() {
    return (
        <GuidesPage
            seoData={seoContent.tidal}
            sections={
                <div className="bg-white dark:bg-zinc-950 text-gray-900 dark:text-white">
                    <div className="max-w-screen-xl pt-20 px-4 py-8 mx-auto lg:py-24 lg:px-6 lg:pt-28">
                        <section className="bg-white dark:bg-zinc-950">
                            <div className="max-w-screen-md mx-auto mb-8 text-center lg:mb-12">
                                <h1 className="mb-4 text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white lg:text-5xl">
                                    Transfer and Sync Tidal Playlists
                                </h1>
                                <p className="mb-8 font-light text-gray-500 sm:text-xl dark:text-gray-400">
                                    High-fidelity streaming with millions of tracks, curated playlists, and artist-first
                                    experiences—take your Tidal library anywhere with Music Porter.
                                </p>
                            </div>
                        </section>

                        <section className="bg-white dark:bg-zinc-950 py-12">
                            <div className="max-w-screen-xl px-4 mx-auto lg:px-6">
                                <div className="max-w-screen-md mx-auto text-center">
                                    <p className="text-lg leading-relaxed text-gray-600 dark:text-gray-300 mb-4">
                                        Tidal is built for listeners who care about sound quality and supporting artists.
                                        Whether you are consolidating accounts or moving to or from another service,
                                        Music Porter helps you move playlists, albums, and saved tracks without starting
                                        from scratch.
                                    </p>
                                    <p className="text-lg text-gray-600 dark:text-gray-300">
                                        Open Tidal here:{" "}
                                        <a
                                            href="https://tidal.com"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-purple-600 dark:text-purple-400 underline font-medium hover:text-purple-700 dark:hover:text-purple-300"
                                        >
                                            Tidal
                                        </a>
                                    </p>
                                </div>
                            </div>
                        </section>

                        <section className="bg-gray-50 dark:bg-zinc-900">
                            <div className="max-w-screen-xl px-4 py-16 mx-auto lg:py-24 lg:px-6">
                                <div className="max-w-screen-md mx-auto text-center mb-12">
                                    <h2 className="mb-4 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                                        Bring your collection to Tidal—or move it elsewhere
                                    </h2>
                                    <p className="text-gray-500 dark:text-gray-400">
                                        Connect Tidal alongside Spotify, Apple Music, YouTube Music, or YouTube.{" "}
                                        <span className="font-semibold text-purple-600 dark:text-purple-400">
                                            Music Porter
                                        </span>{" "}
                                        keeps transfers and optional sync straightforward.
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                    <div className="text-center">
                                        <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white font-bold text-xl">
                                            1
                                        </div>
                                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                                            Connect accounts
                                        </h3>
                                        <p className="text-gray-500 dark:text-gray-400">
                                            Link Tidal and your other music services in Music Porter to authorize
                                            transfers.
                                        </p>
                                    </div>
                                    <div className="text-center">
                                        <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white font-bold text-xl">
                                            2
                                        </div>
                                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                                            Select content
                                        </h3>
                                        <p className="text-gray-500 dark:text-gray-400">
                                            Choose playlists, albums, and tracks to move between Tidal and other
                                            platforms.
                                        </p>
                                    </div>
                                    <div className="text-center">
                                        <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white font-bold text-xl">
                                            3
                                        </div>
                                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                                            Transfer &amp; sync
                                        </h3>
                                        <p className="text-gray-500 dark:text-gray-400">
                                            Run your transfer and optionally enable sync so libraries stay aligned.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="bg-gray-50 dark:bg-zinc-900 py-12">
                            <div className="max-w-screen-xl px-4 mx-auto lg:px-6">
                                <div className="max-w-screen-md mx-auto text-center mb-12">
                                    <h2 className="mb-4 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                                        Transfer your music
                                    </h2>
                                    <p className="text-gray-500 dark:text-gray-400">
                                        Move playlists between Tidal and other major streaming services.
                                    </p>
                                </div>

                                <div className="mb-8">
                                    <h3 className="text-2xl font-bold mb-6 text-center">
                                        Transfer from other services to Tidal
                                    </h3>
                                    <LinksFromStreamtoStreamForTransfer
                                        links={TransferLinksDataFromStreamtoStream["tidal"].to}
                                    />
                                </div>

                                <div className="mb-8">
                                    <h3 className="text-2xl font-bold mb-6 text-center">
                                        Transfer from Tidal to other services
                                    </h3>
                                    <LinksFromStreamtoStreamForTransfer
                                        links={TransferLinksDataFromStreamtoStream["tidal"].from}
                                    />
                                </div>
                            </div>
                        </section>

                        <section className="bg-gray-50 dark:bg-zinc-800">
                            <div className="max-w-screen-xl px-4 py-8 mx-auto lg:py-16 lg:px-6">
                                <div className="max-w-screen-sm mx-auto text-center">
                                    <h2 className="mb-4 text-3xl font-extrabold leading-tight tracking-tight text-gray-900 dark:text-white">
                                        Ready to move your Tidal library?
                                    </h2>
                                    <p className="mb-6 font-light text-gray-500 dark:text-gray-400 md:text-lg">
                                        Join listeners who use Music Porter to switch platforms without losing their
                                        playlists.
                                    </p>
                                    <div className="space-y-4 sm:flex sm:space-y-0 sm:space-x-4 justify-center">
                                        <a
                                            href={config.signupPath}
                                            className="inline-flex items-center justify-center w-full px-5 py-3 text-sm font-medium text-center text-white bg-purple-700 border border-transparent rounded-lg sm:w-auto hover:bg-purple-800 focus:ring-4 focus:ring-purple-300 dark:bg-purple-600 dark:hover:bg-purple-700 dark:focus:ring-purple-800"
                                        >
                                            Sign up for free
                                        </a>
                                        <a
                                            href="/sync-playlists"
                                            className="text-purple-700 bg-white border border-purple-700 hover:bg-purple-50 focus:ring-4 focus:ring-purple-300 font-medium rounded-lg text-sm px-8 py-3 dark:text-purple-400 dark:border-purple-400 dark:hover:bg-purple-900/20 focus:outline-none dark:focus:ring-purple-800 transition-colors duration-200"
                                        >
                                            Learn about sync
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="bg-white dark:bg-zinc-950 py-12">
                            <div className="max-w-screen-xl px-4 mx-auto lg:px-6">
                                <div className="max-w-screen-md mx-auto text-center mb-12">
                                    <h2 className="mb-4 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
                                        Keep playlists in sync
                                    </h2>
                                    <p className="text-gray-500 dark:text-gray-400">
                                        After a transfer, set up sync so updates on one service roll through to the
                                        other.
                                    </p>
                                </div>

                                <div className="mb-8">
                                    <h3 className="text-2xl font-bold mb-6 text-center">
                                        Sync playlists from Tidal to other services
                                    </h3>
                                    <LinksFromStreamtoStreamForSync links={TransferLinksDataFromStreamtoStream["tidal"].from} />
                                </div>

                                <div className="mb-8">
                                    <h3 className="text-2xl font-bold mb-6 text-center">
                                        Sync playlists from other services to Tidal
                                    </h3>
                                    <LinksFromStreamtoStreamForSync links={TransferLinksDataFromStreamtoStream["tidal"].to} />
                                </div>
                            </div>
                        </section>
                    </div>

                    <section className="bg-gray-50 dark:bg-zinc-800">
                        <div className="max-w-screen-xl px-4 py-16 mx-auto lg:py-24 lg:px-6">
                            <div className="max-w-screen-md mx-auto text-center">
                                <h2 className="mb-6 text-3xl font-extrabold leading-tight tracking-tight text-gray-900 dark:text-white lg:text-4xl">
                                    Keep Tidal and the rest of your library aligned
                                </h2>
                                <p className="mb-8 font-light text-gray-500 dark:text-gray-400 md:text-lg">
                                    Transfers plus optional daily sync help you stay organized across platforms.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                                    <a
                                        href={config.signupPath}
                                        className="text-white bg-purple-700 hover:bg-purple-800 focus:ring-4 focus:ring-purple-300 font-medium rounded-lg text-sm px-8 py-3 dark:bg-purple-600 dark:hover:bg-purple-700 focus:outline-none dark:focus:ring-purple-800 transition-colors duration-200"
                                    >
                                        Start now
                                    </a>
                                    <a
                                        href="/transfer-playlists-and-songs"
                                        className="text-purple-700 bg-white border border-purple-700 hover:bg-purple-50 focus:ring-4 focus:ring-purple-300 font-medium rounded-lg text-sm px-8 py-3 dark:text-purple-400 dark:border-purple-400 dark:hover:bg-purple-900/20 focus:outline-none dark:focus:ring-purple-800 transition-colors duration-200"
                                    >
                                        Learn about transfer
                                    </a>
                                </div>
                                <div className="mt-8 text-sm text-gray-500 dark:text-gray-400">
                                    <p>✓ No credit card required • ✓ Free forever transfer • ✓ Lifetime plan available</p>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            }
        />
    );
}
